import React, {
  useContext,
  useState,
  useEffect,
  createContext,
  Dispatch,
  SetStateAction
} from 'react';
import { get, post, put } from '../../redesign/javascript/utils/fetch';
import usePusherSubscribe from 'appJS/hooks/usePusherSubscribe';
import { Event, Subscription } from 'appJS/models/Pusher';
import { v4 as uuid } from 'uuid';
import { UploadChangeParam } from 'antd/lib/upload';
import { message } from 'antd';
import { UploadFile } from 'antd/lib/upload/interface';
import { getFileInfo } from 'appJS/utils/fileListOperations';
import { SortBy } from '../../redesign/clients/design_assets/AssetSortMenuItems';
import track, { trackPage } from 'appJS/utils/analytics';
import * as translations from '../../redesign/clients/design_assets/strings';
import { getLangKey } from 'appJS/models/Language';
import {
  AssetProject,
  DesignAsset,
  DesignAssetsProviderProps,
  EventParams,
  Status,
  UploadAsset
} from '../../redesign/clients/design_assets/types';
import camelcaseKeys from 'camelcase-keys';
import { FileType } from 'appJS/utils/fileListOperations';
import uploadToS3 from 'appJS/utils/uploadToS3';
import { handleSharingClick } from '../../redesign/clients/design_assets/handleSharingClick';
import { useOrganizationState } from './Organization';
import {
  DEFAULT_DESIGN_ASSET_SORT,
  searchAndSortDesignAssets
} from './DesignAssets/searchAndSortDesignAssets';
import {
  ASSET_VERSIONING_EXPERIMENT,
  AssetVersioningProvider
} from '../../redesign/clients/design_assets/AssetVersioningContext';

// @ts-ignore
const DesignAssetsStateContext = createContext();
const strings = translations[getLangKey()];

export const PLACEHOLDER_SURVEY_FEEDBACK_ID = 'Y4aOT6G3';
export const BETA_USER_SURVEY_FEEDBACK_ID = 'fvvWxOY9';
export const ASSETS_SUPPORT_ARTICLE_ID = '84911';
const MAX_FILE_SIZE = 20485760;
const FIGMA_API_URL = 'https://api.figma.com/v1';

const DesignAssetsProvider = (props: DesignAssetsProviderProps) => {
  const {
    project,
    figmaClientId,
    figmaBasicCredentials,
    figmaCallbackURI,
    figmaRefreshToken,
    bugherdUrl,
    container,
    boardContainer,
    currentUserId,
    children,
    viewGuestKanban,
    isGuest
  } = props;
  const [figmaUserToken, setFigmaUserToken] = useState<string | null>(
    props.figmaUserToken
  );
  const [localFigmaRefreshToken, setLocalFigmaRefreshToken] = useState<
    string | null
  >(figmaRefreshToken || null);
  const [data, setData] = useState<DesignAsset[]>([]);
  const [groupOrder, setGroupOrder] = useState<string[]>([]);
  const [status, setStatus] = useState<Status>(Status.LOADING);
  const [surveyId, setSurveyId] = useState<string>(
    BETA_USER_SURVEY_FEEDBACK_ID
  );
  const [showAssets, setShowAssets] = useState(
    window.location.pathname.includes('assets') ||
      window.location.pathname.includes('home')
  );
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const [isFigmaModalOpen, setIsFigmaModalOpen] = useState<boolean>(false);
  const [reconnectAssetId, setReconnectAssetId] = useState<number | null>(null);
  const [isWebsiteModalOpen, setIsWebsiteModalOpen] = useState<boolean>(false);
  const [isGroupNameModalOpen, setIsGroupNameModalOpen] = useState<
    number | false
  >(false);
  const [groupName, setGroupName] = useState<string>('');
  const [shareItem, setShareItem] = useState<number | string | null>(null);
  const [isUploadingModalOpen, setIsUploadingModalOpen] = useState<boolean>(
    false
  );
  const [needsToUpdateCache, setNeedsToUpdateCache] = useState<boolean>(true);
  const [sortBy, setSortBy] = useState<SortBy | null>(
    DEFAULT_DESIGN_ASSET_SORT
  );
  const [search, setSearch] = useState<string>('');
  const [checkingToken, setCheckingToken] = useState<boolean>(false);
  const [figmaUser, setFigmaUser] = useState<{
    handle: string;
    email: string;
    imgUrl: string;
  } | null>(null);

  const trackAssetsButton = (type: string) => {
    track('Assets upload button clicked', {
      organization: project?.organizationId,
      project: project?.id,
      context: type
    });
  };

  const handleSearchAndSort = (): DesignAsset[] =>
    searchAndSortDesignAssets(data, search, sortBy);

  const { hasOrganizationExperiment } = useOrganizationState();
  const hasAssetVersioningExperiment = hasOrganizationExperiment(
    ASSET_VERSIONING_EXPERIMENT
  );

  const { config, pusherChannelName } = project;

  const constructAssetName = (fileName: string): string =>
    fileName
      .split('.')
      .slice(0, -1)
      .join('.');

  const handleError = error => console.error(error);

  const handlePusherEvent = (
    { asset }: { asset: DesignAsset },
    handleEvent: ({
      //eslint-disable-next-line
      assetsClone,
      existingAsset,
      asset
    }: EventParams) => DesignAsset[]
  ) => {
    asset = camelcaseKeys(asset, { deep: true });
    setData(previousValue => {
      const assetsClone = previousValue.slice();
      const existingAsset = assetsClone.find(({ id }) => id === asset.id);
      return handleEvent({ assetsClone, existingAsset, asset });
    });
  };

  const onAssetCreate = ({
    assetsClone,
    existingAsset,
    asset
  }: EventParams): DesignAsset[] =>
    (!existingAsset && [...assetsClone, asset]) || assetsClone;

  const onAssetUpdate = ({
    assetsClone,
    existingAsset,
    asset
  }: EventParams): DesignAsset[] => {
    if (existingAsset) Object.assign(existingAsset, asset);
    return assetsClone;
  };

  const onAssetDelete = ({ assetsClone, asset }: EventParams): DesignAsset[] =>
    assetsClone.filter(({ id }) => id !== asset.id);

  usePusherSubscribe({
    pusher: {
      config,
      projects: [{ id: String(project.id), pusherChannelName }],
      events: [
        {
          name: Event.ASSET_CREATE,
          onUpdate: pusherData => handlePusherEvent(pusherData, onAssetCreate)
        },
        {
          name: Event.ASSET_UPDATE,
          onUpdate: pusherData => handlePusherEvent(pusherData, onAssetUpdate)
        },
        {
          name: Event.ASSET_DELETE,
          onUpdate: pusherData => handlePusherEvent(pusherData, onAssetDelete)
        }
      ],
      subscription: Subscription.USER_ASSETS
    },
    loading: false
  });

  useEffect(() => {
    (async () => {
      if (!needsToUpdateCache) return;
      try {
        const [response, groupOrderResponse] = await Promise.all([
          get(`/projects/${project.id}/assets?format=json`),
          get(`/projects/${project.id}/assets/group_order`)
        ]);
        setData(
          response.map(asset => ({
            ...camelcaseKeys(asset, { deep: true }),
            name: constructAssetName(asset.file_name) || asset.file_name
          }))
        );
        setGroupOrder(groupOrderResponse.group_order || []);
        setStatus(Status.SUCCESS);
      } catch (error) {
        console.error(error);
        setStatus(Status.ERROR);
      } finally {
        setNeedsToUpdateCache(false);
      }
    })();
  }, [project.id, needsToUpdateCache]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('figma_connected') === 'true') {
      const url = new URL(window.location.href);
      url.searchParams.delete('figma_connected');
      window.history.replaceState({}, '', url.toString());
      handleOpenFigma();
    }
  }, []);

  useEffect(() => {
    if (isModalOpen && surveyId === PLACEHOLDER_SURVEY_FEEDBACK_ID) {
      trackPage('Design assets survey', {
        project_id: project.id
      });
    }
  }, [isModalOpen]);

  const uploadAsset: UploadAsset = params => {
    const { onComplete } = params;
    uploadToS3({
      ...params,
      onComplete: async (
        file: FileType,
        _S3URL: string,
        key: string,
        callbackUrl: string,
        is_figma?: boolean
      ) => {
        onComplete();
        try {
          const payload = {
            key,
            file_name: file.name,
            is_figma: !!is_figma,
            ...(hasAssetVersioningExperiment && {
              figma_node_id: params.figmaNodeId,
              figma_file_key: params.figmaFileKey
            })
          };
          if (params.assetId && hasAssetVersioningExperiment) {
            await put(`${callbackUrl}/${params.assetId}`, payload);
          } else {
            await post(callbackUrl, payload);
          }
          setNeedsToUpdateCache(true);
        } catch (error) {
          console.error(error);
          message.error(
            error instanceof Error ? error.message : strings.wentWrong
          );
        }
      }
    });
  };

  const getFigmaData = async (path: string) => {
    return await fetch(`${FIGMA_API_URL}${path}`, {
      headers: {
        Authorization: `Bearer ${figmaUserToken}`
      }
    });
  };

  const openFigmaModal = (open: boolean) => {
    setIsFigmaModalOpen(open);
    if (!open) setReconnectAssetId(null);
  };

  const resyncFigmaAsset = async (asset: DesignAsset) => {
    if (!hasAssetVersioningExperiment || !asset.isFigma) return;
    if (!asset.figmaFileKey || !asset.figmaNodeId) {
      setReconnectAssetId(asset.id);
      await openFigmaPicker();
      return;
    }

    try {
      const response = await getFigmaData(
        `/images/${asset.figmaFileKey}?ids=${asset.figmaNodeId}&format=png`
      );
      const { images } = await response.json();
      const imageResponse = await fetch(images[asset.figmaNodeId]);
      const file = new File([await imageResponse.blob()], `${asset.name}.png`, {
        type: 'image/png'
      });

      uploadAsset({
        assetId: asset.id,
        file: getFileInfo(file as any),
        remove: () => {},
        endpoint: `/projects/${project.id}/assets/new`,
        additionalQueryParams: { file_name: file.name },
        feedback: message.error,
        onStart: () => setIsUploadingModalOpen(true),
        onProgress: () => {},
        onError: () => setIsUploadingModalOpen(false),
        onComplete: () => {
          setIsUploadingModalOpen(false);
          message.success('Figma asset synced.');
        }
      });
    } catch (error) {
      console.error(error);
      message.error(strings.wentWrong);
    }
  };

  const handleRefreshToken = async () => {
    const refreshParams = new URLSearchParams({
      refresh_token: localFigmaRefreshToken || ''
    });
    let response;

    try {
      response = await fetch(`${FIGMA_API_URL}/oauth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Authorization: `Basic ${figmaBasicCredentials}`
        },
        body: refreshParams.toString()
      });

      const httpStatus = response.status;
      response = await response.json();

      const isRefreshTokenInvalid =
        response?.error === 'invalid_grant' ||
        response?.message?.includes('Invalid refresh token') ||
        httpStatus === 400;

      if (isRefreshTokenInvalid) {
        authFigma();
      } else {
        const expiryDate = new Date(
          Date.now() + response.expires_in * 1000
        ).toISOString();

        let userResponse = await put(`/users/${currentUserId}`, {
          figma_token: response.access_token,
          figma_expiry_token: expiryDate
        });
        const { user, error } = await userResponse;
        if (!error) {
          setFigmaUserToken(user.figma_token);
          setCheckingToken(false);
          setIsFigmaModalOpen(true);
        } else {
          message.error(error);
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const authFigma = () => {
    const callbackId = uuid();
    const oauthParms = new URLSearchParams({
      client_id: figmaClientId,
      redirect_uri: figmaCallbackURI,
      scope: 'current_user:read,file_content:read',
      state: callbackId,
      response_type: 'code'
    });

    get(`/projects/${project.id}/auth_figma?callback_id=${callbackId}`)
      .then(({ status }) => {
        if (status === 200) {
          setCheckingToken(false);
          window.open(`https://www.figma.com/oauth?${oauthParms}`, '_self');
        }
      })
      .catch(handleError);
  };

  const handleAuthFigma = () => {
    if (localFigmaRefreshToken) {
      handleRefreshToken();
    } else {
      authFigma();
    }
  };

  const reAuthFigma = () => {
    authFigma();
  };

  const disconnectFigma = async (): Promise<void> => {
    try {
      await post(`/users/${currentUserId}/disconnect_figma`, {});
      setFigmaUserToken(null);
      setLocalFigmaRefreshToken(null);
      setFigmaUser(null);
      setIsFigmaModalOpen(false);
      setReconnectAssetId(null);
      message.success('Figma account disconnected successfully.');
    } catch (err) {
      console.error(err);
      message.error(strings.wentWrong);
    }
  };

  const openFigmaPicker = async (): Promise<void> => {
    let response;
    trackAssetsButton('figma');
    try {
      setCheckingToken(true);
      response = await getFigmaData('/me');
      const { id, handle, email, img_url } = await response.json();

      if (id) {
        setFigmaUser({ handle, email, imgUrl: img_url });
        setIsFigmaModalOpen(true);
        setCheckingToken(false);
      } else {
        handleAuthFigma();
      }
    } catch (err) {
      console.error(err);
      setCheckingToken(true);
      message.error(strings.wentWrong);
    }
  };

  const handleOpenFigma = async (): Promise<void> => {
    setReconnectAssetId(null);
    await openFigmaPicker();
  };

  const onUploadChange = (
    event: UploadChangeParam<any>,
    type?: string,
    assetId?: number
  ): void => {
    const file = (event?.file?.originFileObj as any) as UploadFile;

    if (!file) {
      message.error('Unrecognized file type uploaded');
      return;
    }
    if (!file.size || file.size > MAX_FILE_SIZE) {
      message.error(
        `Sorry, the file (${file.name}) is too large. Maximum file size is 20mb.`
      );
      return;
    }
    const _file = getFileInfo(file);

    const fileType =
      file.type === 'application/pdf' ||
      file.name.toLowerCase().endsWith('.pdf')
        ? 'pdf'
        : 'image';
    const replacementAsset =
      hasAssetVersioningExperiment && assetId
        ? data.find(asset => asset.id === assetId)
        : undefined;
    if (
      replacementAsset &&
      (replacementAsset.fileType === 'pdf') !== (fileType === 'pdf')
    ) {
      message.error(
        replacementAsset.fileType === 'pdf'
          ? 'PDF assets can only be replaced with another PDF.'
          : 'Image assets can only be replaced with another image.'
      );
      return;
    }

    trackAssetsButton(fileType);

    const uploadParameters = {
      file: _file,
      remove: () => {},
      endpoint: `/projects/${project.id}/assets/new`,
      additionalQueryParams: { file_name: _file.name },
      feedback: message.error,
      onStart: () => {
        setIsUploadingModalOpen(true);
      },
      onProgress: () => {},
      onError: () => {},
      onComplete: () => setIsUploadingModalOpen(false)
    };
    uploadAsset({
      ...uploadParameters,
      assetId: hasAssetVersioningExperiment ? assetId : undefined
    });
  };

  const onAddToGroup = async (groupName: string, assetId?: number) => {
    if (groupName) {
      const response = await put(
        `${bugherdUrl}/projects/${project.id}/assets/${assetId ||
          isGroupNameModalOpen}`,
        {
          group_name: groupName
        }
      );

      if (response.status === 'ok') {
        message.success(`Asset has been added to group ${groupName}`);
        setGroupName('');
      } else {
        message.error('Something went wrong, please try again.');
      }
    }
  };

  const renameGroup = async (oldGroupName: string, newGroupName: string) => {
    const trimmedGroupName = newGroupName.trim();
    if (!trimmedGroupName) {
      message.error('Please enter a group name.');
      return false;
    }

    const response = await put(
      `${bugherdUrl}/projects/${project.id}/assets/rename_group`,
      {
        group_name: oldGroupName,
        new_group_name: trimmedGroupName
      }
    );

    if (response.status === 'ok') {
      setData(previousValue =>
        previousValue.map(asset =>
          asset.groupName === oldGroupName
            ? { ...asset, groupName: trimmedGroupName }
            : asset
        )
      );
      setGroupOrder(response.group_order || []);
      message.success(`Group renamed to ${trimmedGroupName}.`);
      track('Renamed group');
      return true;
    }

    message.error(response.error || 'Something went wrong, please try again.');
    return false;
  };

  const updateGroupStatus = async (
    groupName: string,
    nextStatus: DesignAsset['status']
  ) => {
    const response = await put(
      `${bugherdUrl}/projects/${project.id}/assets/archive_group`,
      {
        group_name: groupName,
        status: nextStatus
      }
    );

    if (response.status === 'ok') {
      setData(previousValue =>
        previousValue.map(asset =>
          asset.groupName === groupName
            ? { ...asset, status: nextStatus }
            : asset
        )
      );
      setGroupOrder(response.group_order || []);
      message.success(
        nextStatus === 'archived'
          ? `Group ${groupName} has been archived.`
          : `Group ${groupName} has been restored.`
      );
      track(nextStatus === 'archived' ? 'Archived group' : 'Restored group');
      return true;
    }

    message.error(response.error || 'Something went wrong, please try again.');
    return false;
  };

  const reorderGroups = async (groupNames: string[]) => {
    const response = await put(
      `${bugherdUrl}/projects/${project.id}/assets/reorder_groups`,
      {
        group_names: groupNames
      }
    );

    if (response.status === 'ok') {
      setGroupOrder(response.group_order || groupNames);
      message.success('Group order updated.');
      track('Reordered groups');
      return true;
    }

    message.error(response.error || 'Something went wrong, please try again.');
    return false;
  };

  const hasManagementFeaturesExperiment = true;

  const onSharingClick = ({
    assetId,
    siteId,
    assetGroupName
  }: {
    assetId?: string | number;
    siteId?: string | number;
    assetGroupName?: string;
  }) => {
    handleSharingClick({
      assetId,
      siteId,
      assetGroupName,
      callback: setShareItem
    });
  };

  return (
    <AssetVersioningProvider
      value={{
        enabled: hasAssetVersioningExperiment,
        onUploadChange,
        resyncFigmaAsset
      }}
    >
      <DesignAssetsStateContext.Provider
        value={{
          project,
          currentUserId,
          figmaCallbackURI,
          figmaClientId,
          figmaUserToken,
          bugherdUrl,
          container,
          boardContainer,
          checkingToken,
          data,
          setData,
          groupOrder,
          setGroupOrder,
          status,
          setStatus,
          surveyId,
          setSurveyId,
          sortBy,
          setSortBy,
          search,
          setSearch,
          showAssets,
          setShowAssets,
          isModalOpen,
          setIsModalOpen,
          isFigmaModalOpen,
          setIsFigmaModalOpen: hasAssetVersioningExperiment
            ? openFigmaModal
            : setIsFigmaModalOpen,
          reconnectAssetId: hasAssetVersioningExperiment
            ? reconnectAssetId
            : null,
          isWebsiteModalOpen,
          setIsWebsiteModalOpen,
          isUploadingModalOpen,
          setIsUploadingModalOpen,
          uploadAsset,
          figmaUser,
          disconnectFigma,
          handleOpenFigma,
          reAuthFigma,
          onUploadChange,
          resyncFigmaAsset,
          handleSearchAndSort,
          getFigmaData,
          hasManagementFeaturesExperiment,
          shareItem,
          setShareItem,
          onAddToGroup,
          renameGroup,
          updateGroupStatus,
          reorderGroups,
          setIsGroupNameModalOpen,
          setGroupName,
          groupName,
          isGroupNameModalOpen,
          onSharingClick,
          viewGuestKanban,
          isGuest
        }}
      >
        {children}
      </DesignAssetsStateContext.Provider>
    </AssetVersioningProvider>
  );
};

type DesignAssetsState = {
  project: AssetProject;
  currentUserId: number;
  figmaCallbackURI: string;
  figmaClientId: string;
  figmaBasicCredentials: string;
  figmaUserToken: string | null;
  bugherdUrl: string;
  container: HTMLDivElement;
  boardContainer: HTMLDivElement;
  checkingToken: boolean;
  figmaUser: { handle: string; email: string; imgUrl: string } | null;
  disconnectFigma: () => Promise<void>;
  data: DesignAsset[];
  setData: Dispatch<SetStateAction<DesignAsset[]>>;
  groupOrder: string[];
  setGroupOrder: Dispatch<SetStateAction<string[]>>;
  status: Status;
  setStatus: (status: Status) => void;
  surveyId: string;
  setSurveyId: (surveyId: string) => void;
  sortBy: SortBy | null;
  setSortBy: (sortBy: SortBy | null) => void;
  search: string;
  setSearch: (search: string) => void;
  showAssets: boolean;
  setShowAssets: (showAssets: boolean) => void;
  isModalOpen: boolean;
  setIsModalOpen: (isModalOpen: boolean) => void;
  isFigmaModalOpen: boolean;
  setIsFigmaModalOpen: (isFigmaOpen: boolean) => void;
  reconnectAssetId: number | null;
  isWebsiteModalOpen: boolean;
  setIsWebsiteModalOpen: (isWebsiteModalOpen: boolean) => void;
  isUploadingModalOpen: boolean;
  setIsUploadingModalOpen: (isUploadingModalOpen: boolean) => void;
  uploadAsset: (params: { onComplete: () => void }) => void;
  handleOpenFigma: () => Promise<void>;
  reAuthFigma: () => void;
  onUploadChange: (
    event: UploadChangeParam<any>,
    type?: string,
    assetId?: number
  ) => void;
  resyncFigmaAsset: (asset: DesignAsset) => Promise<void>;
  handleSearchAndSort: () => DesignAsset[];
  getFigmaData: (path: string) => Promise<Response>;
  hasManagementFeaturesExperiment: boolean;
  shareItem: number | string | null;
  setShareItem: (shareItem: number | string | null) => void;
  onAddToGroup: (groupName: string, assetId?: number) => Promise<void>;
  renameGroup: (oldGroupName: string, newGroupName: string) => Promise<boolean>;
  updateGroupStatus: (
    groupName: string,
    nextStatus: DesignAsset['status']
  ) => Promise<boolean>;
  reorderGroups: (groupNames: string[]) => Promise<boolean>;
  isGroupNameModalOpen: number | false;
  setIsGroupNameModalOpen: (isGroupNameModalOpen: number | false) => void;
  groupName: string;
  setGroupName: (groupName: string) => void;
  viewGuestKanban: boolean;
  onSharingClick: ({
    assetId,
    siteId,
    assetGroupName
  }: {
    assetId?: string | number;
    siteId?: string | number;
    assetGroupName?: string;
  }) => void;
  isGuest: boolean;
};

const useDesignAssetsState = (): DesignAssetsState => {
  const context = useContext(DesignAssetsStateContext);

  if (context === undefined) {
    throw new Error(
      'useDesignAssetsState must be used within a DesignAssetsProvider'
    );
  }

  // @ts-ignore
  return context;
};

export { DesignAssetsProvider, useDesignAssetsState, DesignAssetsState };
